import { HttpContextToken, HttpErrorResponse, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { catchError, finalize, from, switchMap, tap, throwError, timer } from 'rxjs';
import { environment } from '../../environments/environment';
import { inject, Injectable, Injector, runInInjectionContext, signal } from '@angular/core';

import { SnackBarService } from './services/snack-bar.service';
import { CustomerAuth } from '../shared/models/Customer';
import { RequestError } from '../../smoothr-web-app-core/models/request-error.model';
import { UserApiService } from '../state/user/user-api.service';
import { UserStateService } from '../state/user/user-state';

export const SUCCESS_BANNER = new HttpContextToken<string | null>(() => null);
export const FAIL_BANNER = new HttpContextToken<string | null>(() => null);
export const SKIP_ERROR_BANNER = new HttpContextToken<boolean>(() => false);

export const loading = signal(false);
export const validationApiRequestError = signal<RequestError[]>([]);

@Injectable()
export class MainInterceptor implements HttpInterceptor {
	private snackBarSvc = inject(SnackBarService);
	private injector = inject(Injector);

	constructor() {}

	intercept(req: HttpRequest<any>, next: HttpHandler) {
		loading.set(true);

		const url = new URL(req.url, location.origin);

		const customerAuth: CustomerAuth = JSON.parse(localStorage.getItem('customer-auth'));

		const headers: any = {
			url: req.url.includes('assets') ? req.url : `${environment.baseUrl}${req.url}`,
			setHeaders: {},
		};
		if (customerAuth?.token && !url.pathname.startsWith('/assets/'))
			headers.setHeaders.Authorization = `Bearer ${customerAuth.token}`;

		return next.handle(req.clone(headers)).pipe(
			tap(res => {
				const successBanner = req.context.get(SUCCESS_BANNER);
				if (successBanner) this.snackBarSvc.present(successBanner);
			}),
			catchError((error: HttpErrorResponse) => {
				if (!req.context.get(SKIP_ERROR_BANNER)) {
					const failBanner = req.context.get(FAIL_BANNER);
					failBanner ? this.snackBarSvc.present(failBanner) : this.snackBarSvc.presentRequestError(error);
				}

				if (error.status === 422) {
					validationApiRequestError.set(error.error.details || error.error?.errors?.error?.details);
				}

				if (error?.status === 401 && (req.url.includes('auth/logout') || req.url.includes('auth/login'))) {
					return throwError(() => error);
				}

				if (error.status === 401) {
					return runInInjectionContext(this.injector, () => {
						const userApiSvc = inject(UserApiService);
						const userStateSvc = inject(UserStateService);

						return from(userApiSvc.refreshToken(customerAuth.refreshToken)).pipe(
							switchMap((res: CustomerAuth) => {
								userStateSvc.customerAuth$.next(res);
								const retryReq = req.clone({
									setHeaders: {
										Authorization: `Bearer ${res.token}`,
									},
								});

								return timer(200).pipe(switchMap(() => next.handle(retryReq)));
							})
						);
					});
				}

				/////SENTRY////

				// const originalRequest = error?.error?.config ?? req; // fallback on Angular request
				// const response = error?.error?.response;
				//
				// const method = (originalRequest.method || req.method)?.toUpperCase();
				//
				// const sentryError = new Error(`${method} ${originalRequest.url} → ${response?.status ?? 'NO_RESPONSE'}`);
				// sentryError.name = `HTTP ${method}`;
				//
				// Sentry.withScope(scope => {
				// 	scope.setFingerprint([
				// 		'axios-like-http',
				// 		method,
				// 		originalRequest.url,
				// 		String(response?.status ?? 'NO_RESPONSE'),
				// 	]);
				//
				// 	scope.setContext('http', {
				// 		url: originalRequest.url,
				// 		method,
				// 		status: response?.status,
				// 		body: originalRequest?.body ?? originalRequest?.data,
				// 		timeout: originalRequest.timeout,
				// 	});
				//
				// 	if (response?.data) {
				// 		scope.setContext('response', {
				// 			message: response.data?.message,
				// 			code: response.data?.code,
				// 		});
				// 	}
				//
				// 	Sentry.captureException(sentryError);
				// });

				return throwError(() => error);
			}),
			finalize(() => loading.set(false))
		);
	}
}
